import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { useSEO } from "@/lib/useSEO";
import { pickLocale } from "@/lib/localize";
import { getCategory } from "@/lib/libraryCategories";
import { fetchCategoryEntries } from "@/lib/libraryApi";
import { LibraryBreadcrumb, breadcrumbJsonLd } from "@/components/LibraryBreadcrumb";
import VirtuesCompass from "./VirtuesCompass";

/**
 * One collection of the Living Library. Phase 1: title, description and
 * a dignified curated-empty state. When entries are published they render
 * automatically — no redesign needed.
 */
export default function LibraryCategory() {
  const { categoryId } = useParams();
  const { t, lang } = useT();
  const category = getCategory(categoryId);
  const [entries, setEntries] = useState(null); // null = loading

  useEffect(() => {
    if (!category) return;
    let alive = true;
    setEntries(null);
    fetchCategoryEntries(category.id)
      .then((data) => { if (alive) setEntries(data.entries || []); })
      .catch(() => { if (alive) setEntries([]); });
    return () => { alive = false; };
  }, [category]);

  useSEO({
    title: category
      ? `${t(`library.cats.${category.id}.title`)} | ${t("library.hero.title")} | StoicOs.ai`
      : "StoicOs.ai",
    description: category ? t(`library.cats.${category.id}.desc`) : "",
    canonical: category ? `https://stoicos.ai/library/${category.id}` : undefined,
    og: { image: "https://stoicos.ai/og-library.jpg" },
    jsonLd: category ? [
      breadcrumbJsonLd([
        { label: t("academy.nav"), href: "https://stoicos.ai/academy" },
        { label: t("library.hero.title"), href: "https://stoicos.ai/library" },
        { label: t(`library.cats.${category.id}.title`), href: `https://stoicos.ai/library/${category.id}` },
      ]),
    ] : undefined,
  });

  if (!category) return <Navigate to="/library" replace />;
  if (category.id === "virtues") return <VirtuesCompass />;
  const Icon = category.Icon;

  return (
    <main data-testid="library-category-page" className="bg-ink text-bone min-h-screen page-fade-in">
      <section className="relative pt-40 md:pt-52 pb-28 md:pb-40 px-6 sm:px-10">
        <div className="max-w-3xl mx-auto">

          <div className="mb-12">
            <LibraryBreadcrumb
              items={[
                { label: t("academy.nav"), to: "/academy" },
                { label: t("library.hero.title"), to: "/library" },
                { label: t(`library.cats.${category.id}.title`) },
              ]}
            />
          </div>

          <div className="flex items-center gap-5">
            <Icon size={30} strokeWidth={1.25} className="text-gold shrink-0" aria-hidden />
            <h1 data-testid="library-category-title" className="font-serif text-4xl sm:text-5xl text-bone leading-[1.08] tracking-tight">
              {t(`library.cats.${category.id}.title`)}
            </h1>
          </div>
          <p className="mt-6 text-base md:text-lg text-fog font-reading leading-[1.9]">
            {t(`library.cats.${category.id}.desc`)}
          </p>

          <div className="gold-rule w-24 my-12" aria-hidden />

          {/* Entries or the curated empty state */}
          {entries === null ? (
            <p className="text-sm text-fog italic font-serif" data-testid="library-category-loading">…</p>
          ) : entries.length > 0 ? (
            <ul data-testid="library-category-entries" className="flex flex-col gap-4">
              {entries.map((e) => (
                <li key={e.id || e.slug}>
                  <Link
                    to={category.id === "virtues" ? `/library/${e.slug}` : `/library/${category.id}/${e.slug}`}
                    className="block border border-white/10 hover:border-gold/40 p-6
                      transition-[transform,box-shadow,border-color] duration-300
                      hover:-translate-y-0.5 hover:shadow-[0_14px_40px_-22px_rgba(232,200,121,0.3)]"
                  >
                    <h3 className="font-serif text-xl text-bone">{pickLocale(e.title, lang)}</h3>
                    {e.summary && (
                      <p className="mt-2 text-sm text-fog font-reading leading-relaxed">
                        {pickLocale(e.summary, lang)}
                      </p>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div
              data-testid="library-category-empty"
              className="border border-gold/20 bg-gold/[0.02] px-8 py-14 text-center"
            >
              <p className="text-[10px] tracking-[0.32em] uppercase text-gold/80">
                {t("library.category.emptyEyebrow")}
              </p>
              <p className="mt-6 font-serif text-xl md:text-2xl text-bone/90 leading-relaxed max-w-md mx-auto">
                {t("library.category.empty")}
              </p>
            </div>
          )}

          <div className="mt-14 flex items-center justify-between gap-6">
            <Link
              to="/library"
              data-testid="library-category-back"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-bone/60 hover:text-gold transition-colors"
            >
              <ArrowLeft size={13} strokeWidth={1.75} />
              {t("library.category.back")}
            </Link>
            <Link
              to="/library/search"
              data-testid="library-category-to-search"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-bone/60 hover:text-gold transition-colors"
            >
              {t("library.cats.search.title")}
              <ArrowRight size={13} strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
